import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getMonthMatrix(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const prevMonthLastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const daysInPrevMonth = prevMonthLastDay.getDate();
  const startDay = firstDay.getDay();
  const matrix = [];
  let day = 1;
  let nextMonthDay = 1;

  for (let i = 0; i < 6; i++) {
    const week = [];
    for (let j = 0; j < 7; j++) {
      if (i === 0 && j < startDay) {
        // Previous month
        week.push({
          day: daysInPrevMonth - startDay + j + 1,
          prev: true,
        });
      } else if (day > daysInMonth) {
        // Next month
        week.push({
          day: nextMonthDay++,
          next: true,
        });
      } else {
        week.push({
          day: day++,
        });
      }
    }
    matrix.push(week);
    if (day > daysInMonth && nextMonthDay > 7) break;
  }
  return matrix;
}

function isSameDay(a, b) {
  return (
    a && b &&
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

function isBeforeDay(a, b) {
  return a && b && a.setHours(0,0,0,0) < b.setHours(0,0,0,0);
}

function isAfterDay(a, b) {
  return a && b && a.setHours(0,0,0,0) > b.setHours(0,0,0,0);
}

// Helper function to get all dates between two dates (inclusive)
function getDatesBetween(startDate, endDate) {
  const dates = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  
  while (currentDate <= end) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
}

const Calendar = ({
  initialDate = new Date(),
  minDate,
  maxDate,
  multiple = false,
  selected: controlledSelected,
  onSelect,
  className = '',
  style = {},
}) => {
  const [viewDate, setViewDate] = useState(new Date(initialDate));
  const [internalSelected, setInternalSelected] = useState(multiple ? [] : null);
  const [rangeStart, setRangeStart] = useState(null); // For consecutive selection
  const today = new Date();
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const matrix = getMonthMatrix(year, month);

  // Use controlled or internal state
  const isControlled = controlledSelected !== undefined;
  const selectedDates = isControlled ? controlledSelected : internalSelected;

  // Reset internal state when multiple prop changes
  useEffect(() => {
    if (!isControlled) {
      setInternalSelected(multiple ? [] : null);
    }
    setRangeStart(null);
  }, [multiple, isControlled]);

  const handlePrev = () => {
    setViewDate(new Date(year, month - 1, 1));
  };

  const handleNext = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const handleSelect = (cellDate, isDisabled) => {
    if (isDisabled) return;

    let newSelection;
    
    if (multiple) {
      if (!rangeStart) {
        // First click - start the range
        setRangeStart(cellDate);
        newSelection = [cellDate];
      } else {
        // Second click - complete the range
        const start = rangeStart;
        const end = cellDate;
        
        if (isSameDay(start, end)) {
          // Same date clicked - clear selection
          newSelection = [];
          setRangeStart(null);
        } else {
          // Different dates - create range
          const rangeDates = getDatesBetween(
            start < end ? start : end,
            start < end ? end : start
          );
          newSelection = rangeDates;
          setRangeStart(null); // Reset for next selection
        }
      }
    } else {
      // Single selection
      newSelection = cellDate;
    }

    // Update internal state if not controlled
    if (!isControlled) {
      setInternalSelected(newSelection);
    }

    // Call onSelect callback
    onSelect?.(newSelection);
  };

  const isToday = (d) => isSameDay(d, today);
  
  const isSelected = (d) => {
    if (multiple) {
      return Array.isArray(selectedDates) && selectedDates.some(sel => isSameDay(sel, d));
    } else {
      return isSameDay(selectedDates, d);
    }
  };

  const isInRange = (d) => {
    if (!multiple || !rangeStart || !Array.isArray(selectedDates) || selectedDates.length === 0) {
      return false;
    }
    
    // Check if this date is between rangeStart and the last selected date
    const lastSelected = selectedDates[selectedDates.length - 1];
    const start = rangeStart < lastSelected ? rangeStart : lastSelected;
    const end = rangeStart < lastSelected ? lastSelected : rangeStart;
    
    return d >= start && d <= end;
  };
  
  const isDisabled = (d) =>
    (minDate && isBeforeDay(d, minDate)) || (maxDate && isAfterDay(d, maxDate));

  return (
    <motion.div
      className={`liquid-glass-calendar ${className}`}
      style={{
        maxWidth: 380,
        width: '100%',
        padding: 24,
        borderRadius: 24,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        border: '1px solid rgba(255,255,255,0.18)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        ...style,
      }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <button
          onClick={handlePrev}
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: 'none',
            borderRadius: 8,
            color: '#fff',
            fontSize: 20,
            width: 36,
            height: 36,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s',
          }}
        >
          &#8592;
        </button>
        <div style={{ fontWeight: 600, fontSize: 20, letterSpacing: 1, textShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
          {viewDate.toLocaleString('default', { month: 'long' })} {year}
        </div>
        <button
          onClick={handleNext}
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: 'none',
            borderRadius: 8,
            color: '#fff',
            fontSize: 20,
            width: 36,
            height: 36,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s',
          }}
        >
          &#8594;
        </button>
      </div>
      {/* Days of week */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8, gap: 2 }}>
        {WEEK_DAYS.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontWeight: 500, opacity: 0.7, letterSpacing: 1 }}>{d}</div>
        ))}
      </div>
      {/* Days grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {matrix.map((week, i) =>
          week.map((cell, j) => {
            const cellDate = cell.prev
              ? new Date(year, month - 1, cell.day)
              : cell.next
              ? new Date(year, month + 1, cell.day)
              : new Date(year, month, cell.day);
            const isCellToday = isToday(cellDate);
            const isCellSelected = isSelected(cellDate);
            const isCellInRange = isInRange(cellDate);
            const isCellDisabled = isDisabled(cellDate) || cell.prev || cell.next;
            
            // Determine background based on selection state
            let background;
            if (isCellDisabled) {
              background = 'rgba(255,255,255,0.04)';
            } else if (isCellSelected) {
              background = 'linear-gradient(135deg, #6ee7b7 0%, #3b82f6 100%)';
            } else if (isCellInRange) {
              background = 'rgba(59,130,246,0.3)';
            } else if (isCellToday) {
              background = 'rgba(255,255,255,0.18)';
            } else {
              background = 'rgba(255,255,255,0.09)';
            }

            return (
              <motion.button
                key={i + '-' + j}
                onClick={() => handleSelect(cellDate, isCellDisabled)}
                whileTap={isCellDisabled ? undefined : { scale: 0.92 }}
                disabled={isCellDisabled}
                style={{
                  aspectRatio: '1/1',
                  width: '100%',
                  border: 'none',
                  borderRadius: 12,
                  background,
                  color: isCellDisabled ? 'rgba(255,255,255,0.25)' : '#fff',
                  fontWeight: isCellSelected ? 700 : 500,
                  fontSize: 16,
                  boxShadow: isCellSelected
                    ? '0 2px 12px 0 rgba(59,130,246,0.18)'
                    : isCellToday
                    ? '0 2px 8px 0 rgba(255,255,255,0.10)'
                    : 'none',
                  outline: isCellSelected ? '2px solid #3b82f6' : 'none',
                  outlineOffset: 1,
                  cursor: isCellDisabled ? 'not-allowed' : 'pointer',
                  opacity: isCellDisabled ? 0.5 : 1,
                  transition: 'background 0.2s, color 0.2s, opacity 0.2s',
                  margin: 0,
                  padding: 0,
                  userSelect: 'none',
                }}
              >
                {cell.day}
              </motion.button>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default Calendar; 